/* ============================================================================
   HR "SMART CART" — one purchase choice at a time (owner brief 2026-08-27,
   verbatim: "when someone adds the other type of variant to the cart it would
   remove the other variant automatically from the cart... smooth").

   This product is ONE product with TWO variants (60-Day, 30-Day), and the
   60-Day variant can be bought one-time OR on the subscription selling plan.
   Shopify treats variant+selling-plan as separate cart LINES, so switching
   from one-time to subscription (or 30-Day to 60-Day, etc.) on the PDP does
   not replace the old line, it ADDS a second one. The owner does not want
   two purchase paths for the same product sitting in one cart at once.

   Scope: this file only ever touches lines for THIS product's two known
   variant IDs (see VARIANT_60 / VARIANT_30 below, from HR-CANONICAL-FACTS /
   the owner's brief). It never touches gift lines, upsell lines, or any
   other product in the cart.

   WHY THIS DOES NOT REPLACE snippets/hr-cart-conflict.liquid: that banner is
   the progressive-enhancement fallback for a shopper with JS blocked or a
   request that lands between this script loading and the add completing. In
   the normal case this file resolves the conflict before that banner would
   ever have a chance to render, so the two are belt-and-suspenders, not
   duplicate work -- the banner NEVER mutates the cart itself, this does.

   SEQUENCING WITH assets/hr-cart-hooray.js (the auto-discount script): that
   file must decide "is this cart subscription-only" AFTER any conflicting
   line here has been removed, never before, or a one-time line that is about
   to be superseded could fool it into applying/keeping a discount code that
   is about to become a ghost the instant this script's removal lands. So
   this file dispatches `hr:cart-settled` on `document` once it has finished
   (whether or not it actually removed anything), and hr-cart-hooray.js
   listens for THAT event instead of the raw add event for its own logic.
   ========================================================================== */
(function () {
  "use strict";

  /* ==========================================================================
     KNOWN VARIANTS — SINGLE SOURCE OF TRUTH for this file.
     gid://shopify/ProductVariant/43659971952694 = 60-Day (60 pouches), $240,
       buyable one-time or on selling plan 2261450806 ("2-Month Supply, every
       8 weeks").
     gid://shopify/ProductVariant/46913828388918 = 30-Day (30 pouches), $120,
       one-time only (no subscription option exists on this variant today).
     ========================================================================== */
  var VARIANT_60 = 43659971952694;
  var VARIANT_30 = 46913828388918;
  var KNOWN_VARIANTS = [VARIANT_60, VARIANT_30];

  /* Same root-resolution pattern as assets/hr-cart-hooray.js's fetchCart(), so
     both files agree on where "the cart" lives on a localized/multi-domain
     store: prefer Shopify's own routes.root, fall back to the theme's
     window.routes, fall back to a bare relative path. */
  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function fetchCart() {
    return fetch(shopifyRoot() + "cart.js", { headers: { Accept: "application/json" } }).then(function (r) {
      return r.json();
    });
  }

  function cartChangeUrl() {
    return (window.routes && window.routes.cart_change_url) || shopifyRoot() + "cart/change.js";
  }

  /* A "purchase signature" is what makes two lines of the SAME variant
     genuinely different purchases: the selling plan (or lack of one). Two
     lines with the same signature are just split quantity, never a conflict. */
  function purchaseKey(item) {
    var planId =
      item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? item.selling_plan_allocation.selling_plan.id
        : "onetime";
    return item.variant_id + ":" + planId;
  }

  function isSubscriptionLine(item) {
    return !!(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
  }

  /* WHICH LINE IS "THE ONE JUST PICKED" (fix, 2026-08-27 live testing).
     The first version of this file assumed Shopify always appends a newly
     added line to the END of cart.items, and kept whichever relevant line
     was last in that array. REPRODUCED LIVE that this assumption is false:
     adding the subscription on top of an existing one-time line did not
     reliably land the new line last, so the wrong line was kept and the
     switch notice named the wrong purchase.

     Robust fix: read what the shopper ACTUALLY just chose straight off the
     PDP's own hidden form inputs (`product-form [name="id"]` / `[name=
     "selling_plan"]`), which hr-subscription-box.js keeps in sync with
     whichever plan card is selected and which product-form.js submits
     verbatim on Add to Cart -- these do not depend on cart-array ordering at
     all. A disabled/empty selling_plan input means the last submit was
     one-time (Dawn omits a disabled field from FormData, which is exactly
     why hr-subscription-box.js disables it for a one-time pick). Read-only:
     this never writes to the form, only interprets its current state. */
  function readJustSubmittedSignature() {
    try {
      var idInput = document.querySelector('product-form [name="id"]');
      if (!idInput || !idInput.value) return null;
      var variantId = parseInt(idInput.value, 10);
      if (!variantId) return null;
      var planInput = document.querySelector('product-form [name="selling_plan"]');
      var planId = "onetime";
      if (planInput && !planInput.disabled && planInput.value) planId = planInput.value;
      return variantId + ":" + planId;
    } catch (e) {
      return null;
    }
  }

  function purchaseLabel(item) {
    if (isSubscriptionLine(item)) return "the subscription";
    if (item.variant_id === VARIANT_30) return "the 30-Day one-time purchase";
    return "the 60-Day one-time purchase";
  }

  /* ---- the notice card (mirrors assets/hr-cart-hooray.js's buildCard/showPopup,
     own class names, own host, so the two can never collide) ---------------- */
  function emptyEl(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  function buildCloseIcon() {
    var svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    var p1 = document.createElementNS("http://www.w3.org/2000/svg", "path");
    p1.setAttribute("d", "M5 5l14 14M19 5L5 19");
    svg.appendChild(p1);
    return svg;
  }

  function showSwitchNotice(newItem, removedItems) {
    var host = document.getElementById("hr-cart-switch");
    if (!host) return;
    emptyEl(host);
    host.hidden = false;
    host.setAttribute("aria-hidden", "false");

    var card = document.createElement("div");
    card.className = "hr-cart-switch__card";
    card.setAttribute("role", "status");

    var closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "hr-cart-switch__close";
    closeBtn.setAttribute("aria-label", "Dismiss");
    closeBtn.appendChild(buildCloseIcon());

    var head = document.createElement("p");
    head.className = "hr-cart-switch__head";
    head.textContent = "Switched to " + purchaseLabel(newItem);

    var removedLabels = removedItems.map(purchaseLabel);
    // De-duplicate in case two removed lines share a label (should not happen
    // in practice, one product only ever has 3 purchase signatures, but never
    // print the same clause twice if it does).
    var uniqueRemoved = removedLabels.filter(function (l, i) {
      return removedLabels.indexOf(l) === i;
    });

    var sub = document.createElement("p");
    sub.className = "hr-cart-switch__sub";
    sub.textContent =
      "We removed " +
      uniqueRemoved.join(" and ") +
      " from your cart, so you are only charged for the one you just picked.";

    card.appendChild(closeBtn);
    card.appendChild(head);
    card.appendChild(sub);

    function dismiss() {
      card.classList.remove("is-in");
      card.style.opacity = "0";
      setTimeout(function () {
        if (card.parentNode) card.parentNode.removeChild(card);
      }, 260);
    }
    var autoTimer = setTimeout(dismiss, 5200);
    closeBtn.addEventListener(
      "click",
      function () {
        clearTimeout(autoTimer);
        dismiss();
      },
      { once: true }
    );

    host.appendChild(card);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        card.classList.add("is-in");
      });
    });

    try {
      if (typeof window.hrTrack === "function") {
        window.hrTrack("cart_purchase_type_switched", {
          to: newItem.variant_id + (isSubscriptionLine(newItem) ? ":sub" : ":onetime"),
          removed_count: removedItems.length,
        });
      }
    } catch (e) {}
  }

  /* Re-render exactly what the cart line removals changed.

     FOUND LIVE (2026-08-27) while verifying this file: CartDrawerItems
     deliberately overrides onCartUpdate() to a NO-OP (assets/cart-drawer.js,
     owner brief 2026-08-05 -- it used to double-render and produce a visible
     white flash). Calling it here, as an early version of this file did,
     silently does nothing: the drawer kept showing the just-removed line
     and the "is-empty" bookkeeping never updated, even though the real cart
     (per /cart.js) was already correct. cart-discount-codes-custom.js's own
     triggerCartUpdate() avoids the same trap by calling updateQuantity()
     instead, the method this class actually overrides with a real render
     (see hrRender there) -- so this does the same: read the TRUE current
     cart and replay one line's own real quantity back to it. That changes
     nothing server-side (it is already that quantity) but forces exactly
     the same real re-render path a genuine quantity edit takes. Using the
     cart's own freshly-read state (never a guessed line/qty pair) means
     this can never accidentally nudge some unrelated line's quantity. */
  function refreshDrawer() {
    var cartDrawerItems = document.querySelector("cart-drawer-items");
    if (cartDrawerItems && typeof cartDrawerItems.updateQuantity === "function") {
      return fetchCart().then(function (cart) {
        var items = cart.items || [];
        if (!items.length) return;
        cartDrawerItems.updateQuantity(1, items[0].quantity);
      });
    }
    var cartItems = document.querySelector("cart-items");
    if (cartItems && typeof cartItems.onCartUpdate === "function") {
      cartItems.onCartUpdate();
    }
    return Promise.resolve();
  }

  function removeLine(line) {
    return fetch(cartChangeUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ line: line, quantity: 0 }),
    });
  }

  function resolveConflicts() {
    return fetchCart().then(function (cart) {
      var items = cart.items || [];
      var relevant = [];
      items.forEach(function (it, idx) {
        if (KNOWN_VARIANTS.indexOf(it.variant_id) !== -1) {
          relevant.push({ item: it, line: idx + 1, key: purchaseKey(it) });
        }
      });
      if (relevant.length < 2) return false;

      // Prefer matching the PDP's own current form state (see
      // readJustSubmittedSignature above) -- this is what the shopper
      // actually just chose, independent of cart-array ordering. Only fall
      // back to "last in the array" (Shopify's usual but not guaranteed
      // append order) when that signature cannot be read at all or does not
      // match any relevant line (e.g. this add came from somewhere other
      // than this PDP's own form, or the DOM has already moved on).
      var justSubmitted = readJustSubmittedSignature();
      var kept = null;
      if (justSubmitted) {
        var matches = relevant.filter(function (r) {
          return r.key === justSubmitted;
        });
        if (matches.length) kept = matches[0];
      }
      if (!kept) kept = relevant[relevant.length - 1];

      var toRemove = relevant.filter(function (r) {
        return r.key !== kept.key;
      });
      if (!toRemove.length) return false; // same signature repeated -- not a conflict, just quantity

      // Highest line index first: removing a line shifts every LATER index
      // down by one, so working from the top keeps every not-yet-removed
      // line's index stable for the rest of the sequence.
      toRemove.sort(function (a, b) {
        return b.line - a.line;
      });

      var chain = Promise.resolve();
      toRemove.forEach(function (r) {
        chain = chain.then(function () {
          return removeLine(r.line);
        });
      });

      return chain.then(function () {
        refreshDrawer();
        showSwitchNotice(
          kept.item,
          toRemove.map(function (r) {
            return r.item;
          })
        );
        return true;
      });
    });
  }

  function settle() {
    document.dispatchEvent(new CustomEvent("hr:cart-settled"));
  }

  document.addEventListener("hr:cart-item-added", function () {
    if (window.hrCartSmartBusy) return;
    window.hrCartSmartBusy = true;
    // Small delay so the add's own optimistic render (drawer opening, section
    // swap) settles first -- removing a line mid-render fought the drawer's
    // own animation in testing. Matches the delay hr-cart-hooray.js already
    // uses for the same reason.
    setTimeout(function () {
      resolveConflicts()
        .catch(function () {
          // Network/parse failure: never block the shopper's cart over this.
          // The conflict, if any, is still visible via hr-cart-conflict.liquid's
          // static warning as a fallback.
        })
        .then(function () {
          window.hrCartSmartBusy = false;
          settle();
        });
    }, 260);
  });

  function init() {
    // no-op: all behaviour is event-driven above, this just documents the
    // entry point for HRDefer the same way every other cart script does.
  }

  if (window.HRDefer) {
    window.HRDefer(init);
  } else if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
